import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import Button from "@material-ui/core/Button"
import { Dialog, DialogActions, DialogContent, Slide, TextField } from "@material-ui/core"
import ReactQuagga, { useQuagga } from "../ReactQuagga"
import { isHex } from "../../utils/validation"
import API, { graphqlOperation } from "@aws-amplify/api"
import { getMasterLocationByMasterId } from "../../graphql/queries"
import ReactQrReader from "../ReactQrReader/ReactQrReader"
import api, { discardStatuses, HttpMethod } from "../../constants/api"
import offlineStorage from "../../offlineStorage"
import NodeLocationNavigator from "../LayoutMap/LayoutOverlay/NodeLocationNavigator"
import SelectComponent from "./SelectComponent"
import useStyles from "./styles"
import RememberSelectionBtn from "./RememberSelectionBtn"
import ScannerHeaderComponent from "./ScannerHeaderComponent"
import scannerBtnOptions from "./ScannerBtnOptions"
import ErrorOutline from "@material-ui/icons/ErrorOutline"

const Transition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />
})

const DeviceIdInput = memo(
	({
		handleClose,
		handleAccept,
		handleComponentSelection,
		commissioningComponentSelection,
		rememberComponentSelection,
		handleRememberComponentSelection,
		locIds,
		scannerSupported,
		deviceIdEntryMethod,
		setDeviceIdEntryMethod,
		isDeviceIdAssignment,
		nodeDetails,
		masterDetails,
		handleNodeComponentScanned,
		handleMasterComponentScanned,
		nodeComponentScanned,
		masterComponentScanned,
		nodeLocIdFromUrl,
	}) => {
		const styles = useStyles()
		const [loading, setLoading] = useState(false)
		const locIdsSplit = useMemo(() => (locIds.length ? locIds.split(":") : []), [locIds])
		// Info: nodeLocId follows this format - 3 characters with the index e.g. 010.
		const [siteUuid, masterUuid, nodeLocId] = locIdsSplit
		const isMaster = useMemo(() => locIdsSplit.length === 2, [locIdsSplit])
		const label = useMemo(() => (isMaster ? "Master" : "Node"), [isMaster])
		const [scanning, setScanning] = useState(false)
		const [scannedData, setScannedData] = useState()
		const [nodeIdReplacement, setNodeIdReplacement] = useState(false)
		const originalDeviceId = useMemo(
			() => (isMaster ? masterDetails?.[masterUuid]?.mId : nodeDetails?.[masterUuid]?.[nodeLocIdFromUrl]?.nId),
			[isMaster, masterDetails, masterUuid, nodeDetails, nodeLocIdFromUrl],
		)
		const [newDeviceId, setNewDeviceId] = useState("")
		const masterOrNode = isMaster ? "Master" : "Node"
		const [userMessage, setUserMessage] = useState({})
		const [successful, setSuccessful] = useState(false)
		const takenBy = useRef(null)

		useEffect(() => {
			if (successful) {
				takenBy.current = null
			}
		}, [successful])

		// Note: on initial load it'll show the originalDeviceId
		useEffect(() => {
			setNewDeviceId(originalDeviceId || "")
		}, [nodeLocIdFromUrl, originalDeviceId, isMaster])

		const setFeedbackHandler = useCallback((response) => {
			setUserMessage(response)
		}, [])

		const clearFeedbackHandler = useCallback(() => {
			setUserMessage({})
		}, [])

		// Info: Displays the Master name or Node ID selected for scanning
		const nodeControllerUuidToDisplay = useMemo(() => {
			if (!nodeDetails || !nodeDetails[masterUuid] || !nodeDetails[masterUuid][nodeLocIdFromUrl]) return "N/A"
			return nodeDetails[masterUuid][nodeLocIdFromUrl]["nId"] ? nodeDetails[masterUuid][nodeLocIdFromUrl]["nId"] : "N/A"
		}, [masterUuid, nodeDetails, nodeLocIdFromUrl])

		// Info: Handles the selection of a component
		const setComponentSelection = useCallback(
			(component, status) => {
				const scannedComponent = { ...component }
				scannedComponent.timestamp = new Date().toLocaleTimeString()
				scannedComponent.status = status
				scannedComponent.name = commissioningComponentSelection.name

				if (isMaster) {
					scannedComponent.master = masterUuid
					handleMasterComponentScanned({
						componentsScanned: [scannedComponent],
						componentType: commissioningComponentSelection.name,
					})
				}
				if (!isMaster && nodeDetails[masterUuid][nodeLocIdFromUrl]) {
					scannedComponent.node = nodeDetails[masterUuid][nodeLocIdFromUrl].uuid
					handleNodeComponentScanned({
						componentsScanned: [scannedComponent],
						componentType: commissioningComponentSelection.name,
					})
				}
			},
			[
				commissioningComponentSelection,
				isMaster,
				nodeDetails,
				masterUuid,
				nodeLocIdFromUrl,
				handleMasterComponentScanned,
				handleNodeComponentScanned,
			],
		)

		// Info: Handles the selection of a component from component list. Passed to child components as a prop
		const componentListSelectionHandler = useCallback(
			(option) => {
				setScanning(true)
				handleComponentSelection(option)
				setDeviceIdEntryMethod("qrCode")
				setNodeIdReplacement(false)
			},
			[handleComponentSelection, setDeviceIdEntryMethod],
		)

		// Info: Handles the selection between Barcode and QR code scanning. Passed to child components as a prop
		const scannerBtnOptionSelectionHandler = useCallback(
			(type, option) => {
				setScanning(true)
				setDeviceIdEntryMethod(type)
				setNodeIdReplacement(true)
				handleComponentSelection(option)
			},
			[handleComponentSelection, setDeviceIdEntryMethod],
		)

		// Info: handles the text input of the Node ID field. Used in endAdornment useMemo
		const handleSetDeviceId = (deviceId) => {
			setNewDeviceId(deviceId ?? "")
		}

		// Info: must be named 'endAdornment' for the QR code & Barcode icons to appear in the Node Id text input
		const endAdornment = useMemo(() => {
			return scannerBtnOptions(newDeviceId, handleSetDeviceId, scannerSupported, scannerBtnOptionSelectionHandler)
		}, [newDeviceId, scannerBtnOptionSelectionHandler, scannerSupported])

		// Info: Handles the change of the Node ID field
		const handleIdChange = useCallback((e) => {
			let newValue = e.target.value
			if (newValue) {
				// Remove all non-hex characters
				newValue = newValue.replace("0x", "").toUpperCase()
				newValue = newValue.replace(/[^A-F0-9]/g, "")
			}
			setNewDeviceId(newValue)
		}, [])

		const inputValidationErrorText = useMemo(() => {
			if (!newDeviceId) return
			const isValid = isHex(newDeviceId)

			if (!isValid) {
				return "Not a valid hex ID."
			}

			if (!isMaster && newDeviceId.length > 16) {
				return "Max 16 characters."
			}

			if (isMaster && newDeviceId.length < 16) {
				return "Min length of 16 characters."
			}

			if (isMaster && newDeviceId.length > 18) {
				return "Max length of 18 characters."
			}
		}, [newDeviceId, isMaster])

		const inputFormattingError = useMemo(() => {
			if (inputValidationErrorText || !newDeviceId) return true
			const formatter = (val) => val.toUpperCase().padStart(8, "0")

			if (newDeviceId.length > 8) {
				const firstHalf = newDeviceId.slice(0, 8).padStart(8, "0")
				const secondHalf = newDeviceId.slice(8, 16).padStart(8, "0")
				if (formatter(firstHalf) !== firstHalf || formatter(secondHalf) !== secondHalf) {
					return true
				}
			} else {
				if (formatter(newDeviceId) !== newDeviceId.padStart(8, "0")) return true
			}
		}, [inputValidationErrorText, newDeviceId])

		const inputValidationHelpText = useMemo(() => {
			if (inputValidationErrorText) return inputValidationErrorText

			if (newDeviceId === null || inputFormattingError) {
				return `${label} ID - no leading '0x'`
			}
		}, [inputValidationErrorText, newDeviceId, inputFormattingError, label])

		const warning = useMemo(() => {
			if (!newDeviceId) return
			// Check if the new device ID is already taken by another node in the master
			if (!isMaster) {
				// Check if the new device ID is already taken by another node in the master.
				// The last 4 characters of the node ID must be unique within the master.
				const slicedDeviceId = newDeviceId?.slice(-4)
				// Allow unassignment by resetting to 0000
				if (slicedDeviceId && slicedDeviceId !== "0000") {
					const thisMasterNodeDetails = nodeDetails?.[masterUuid] || {}
					const takenNodeIds = new Set(
						Object.values(thisMasterNodeDetails).map((nodeInfo) => (nodeInfo["nId"] || "0000").slice(-4)),
					)
					if (takenNodeIds.has(slicedDeviceId)) {
						const matchingNode = Object.entries(thisMasterNodeDetails).find(([_nodeIndex, nodeInfo]) => {
							return (nodeInfo["nId"] || "0000").slice(-4) === slicedDeviceId && _nodeIndex !== nodeLocIdFromUrl
						})?.[0]
						if (matchingNode === undefined || matchingNode === nodeLocIdFromUrl) {
							// The matching node is *this* node.
							return null
						}
						return "This node ID is already taken in this master by another node. You cannot create duplicate IDs within a Master as it can lead to unexpected behaviors."
					}
				}
			}
		}, [newDeviceId, isMaster, masterUuid, nodeDetails, nodeLocIdFromUrl])

		const handleScanError = useCallback((err) => {
			if (err.message.includes("denied")) {
				alert("Camera permission denied")
			}
			setScanning(false)
		}, [])

		const handleScannerReOpen = useCallback(() => {
			setTimeout(() => {
				setScanning(true)
			}, 1000)
		}, [])

		const handleScannerClose = useCallback(() => {
			setScanning(false)
		}, [])

		const handleScannerOpen = useCallback((status) => {
			setScanning(status)
		}, [])

		// Info: handle re-opening of scanner when another node is selected
		useEffect(() => {
			if (!isDeviceIdAssignment) return
			if (rememberComponentSelection && commissioningComponentSelection.isMaster === isMaster) {
				setScanning(true)
			}
		}, [commissioningComponentSelection, isDeviceIdAssignment, isMaster, rememberComponentSelection])

		const handleCodeDetection = useCallback(
			(data) => {
				setUserMessage({})
				if (deviceIdEntryMethod === "barCode") {
					// Create a synthetic event object, so we can reuse the logic in handleChange for scans
					const e = {
						target: { value: data.codeResult.code },
					}
					handleIdChange(e)
				} else {
					const e = {
						target: { value: data["radioid"] },
					}
					// Old functionality for updating the Radio ID in the old system with DynamoDB
					if (nodeIdReplacement) {
						handleIdChange(e)
					}
					setScannedData(data)
					// INFO: sets scanning to false after a scan attempt. if unsuccessful scanner should be re-opened.
					setScanning(false)
				}
			},
			[deviceIdEntryMethod, handleIdChange, nodeIdReplacement],
		)

		// Info: trigger the Rest API Node importer after assigning a Node ID
		const triggerNodeImporter = useCallback(async () => {
			await api
				.triggerNodeImporterForSpecificSite(siteUuid)
				.catch((ex) => console.error("Error triggering Node importer", ex))
		}, [siteUuid])

		const handleGraphQLSubmit = useCallback(async () => {
			if (!inputValidationErrorText && !warning) {
				setLoading(true)
				try {
					// The user may be offline. If so, graphQL queries will not work. At the same time, we need to avoid duplicate
					// master IDs. For now, we will set a clearUserMessage on the check and allow the user to proceed even if we haven't
					// confirmed that there are no duplicates.
					const deviceIdAlreadyInUsePromise = new Promise((resolve) => {
						API.graphql(graphqlOperation(getMasterLocationByMasterId, { mId: newDeviceId }))
							.then((result) => {
								const items = result.data.getMasterLocationByMasterId.items
								if (items && items.length) {
									const firstItem = items[0]
									takenBy.current = `/layout/${firstItem.layoutId}?view=layout&overlay=alarms&locIds=${firstItem.id}`
								}
								resolve(!!items && !!items.length)
								triggerNodeImporter()
								setFeedbackHandler({
									message: `Successfully replaced ID from ${originalDeviceId} to ${newDeviceId}`,
									type: "success",
								})
							})
							.catch((error) => {
								console.error(error)
								resolve("no-network")
								setSuccessful(false)
							})
					})

					await deviceIdAlreadyInUsePromise.then(async (result) => {
						if (result === "clearUserMessage" || result === "no-network") {
							console.warn(
								"Unable to confirm that this ID is not in use because you are not connected to the internet. " +
									"Proceeding anyway.",
							)
							handleAccept(newDeviceId, nodeLocIdFromUrl)
						} else {
							if (result === true) {
								setFeedbackHandler({
									message: (
										<span>
											{masterOrNode} ID {newDeviceId} is already in use by <a href={takenBy.current}>this controller</a>
											.
										</span>
									),
									type: "error",
								})
								setNewDeviceId(originalDeviceId)
								setSuccessful(false)
							} else {
								handleAccept(newDeviceId, nodeLocIdFromUrl)
								setSuccessful(true)
								if (!isDeviceIdAssignment) {
									setFeedbackHandler({
										message: `Successfully replaced ID from ${originalDeviceId} to ${newDeviceId}`,
										type: "success",
									})
								}
							}
						}
					})
				} finally {
					setLoading(false)
				}
			}
		}, [
			inputValidationErrorText,
			warning,
			newDeviceId,
			triggerNodeImporter,
			handleAccept,
			nodeLocIdFromUrl,
			masterOrNode,
			originalDeviceId,
			setFeedbackHandler,
			isDeviceIdAssignment,
		])

		// Info: PostgreSQL - Saves component information
		useEffect(() => {
			if (!commissioningComponentSelection || !scannedData) return

			const controllerUuid = isMaster ? masterUuid : nodeDetails[masterUuid][nodeLocIdFromUrl].uuid
			if (!controllerUuid) {
				setFeedbackHandler({
					message: "Controller is missing Rest API UUID and will not be saved in the Admin Panel.",
					type: "error",
				})
				return
			}

			if (warning) {
				setFeedbackHandler({
					message: "Duplicate ID: could not be saved to Admin Panel",
					type: "error",
				})
				return
			}

			// Info: selection of the Node Enclosure (QR code btn within the text input box) will also trigger the
			// submission of the radio ID to GraphQL which is the legacy functionality
			if (commissioningComponentSelection.method === "setNodeEnclosure") {
				handleGraphQLSubmit()
			}

			// Scanned data validation
			let missingFields = []
			for (const field of commissioningComponentSelection?.validation) {
				if (!scannedData[field]) {
					missingFields.push(field)
				}
			}
			if (missingFields.length) {
				setComponentSelection(commissioningComponentSelection, "failed")
				const missingFieldsString = missingFields.join(", ")
				const snackbarMessage = `${commissioningComponentSelection?.name} is missing fields: ${missingFieldsString}`
				setFeedbackHandler({
					message: snackbarMessage,
					type: "error",
				})
				handleScannerReOpen()
			}

			const controllerType = isMaster ? "master" : "node"
			scannedData[controllerType] = controllerUuid
			// part_number is always supposed to be _part_number
			// part_number is the FK in the db to an actual type, which this isn't always guaranteed to match
			scannedData["_part_number"] = scannedData["part_number"]
			delete scannedData["part_number"]
			if (scannedData["radioid"]) {
				scannedData["original_radio_id"] = scannedData["radioid"].replace("0x", "").toUpperCase()
				delete scannedData["radioid"]
			}

			if (!missingFields.length) {
				api[commissioningComponentSelection.method](controllerUuid, scannedData)
					.then((response) => {
						if (response?.status === 201) {
							setComponentSelection(commissioningComponentSelection, "success")
							setFeedbackHandler({
								message: `Successfully scanned ${commissioningComponentSelection.name} ${
									scannedData["serial_number"] ?? ""
								}`,
								type: "success",
							})
						} else {
							setFeedbackHandler({
								message: `Failed to save ${commissioningComponentSelection.name} information.`,
								type: "error",
							})
						}
					})
					.catch(async (error) => {
						const dontReopenCondition =
							!commissioningComponentSelection.isMaster && commissioningComponentSelection.name === "enclosure"

						if (error.response?.body?.non_field_errors || error.response?.body?.field_errors) {
							// these are Django serializer errors. There's a possibility of any number of these, under different keys
							// If we have any of these, we want to NOT store the Node Board, and inform the user
							// format is { "non_field_errors": ["inputError message 1", "inputError message 2"], "field_errors": ["inputError message 1", "inputError message 2"] }
							setComponentSelection(commissioningComponentSelection, "failed")
							const nonFieldErrors = error.response?.body?.non_field_errors
							const fieldErrors = error.response?.body?.field_errors
							let errorStrings = [...(nonFieldErrors || []), ...(fieldErrors || [])]
							let message = ""
							for (const value of errorStrings) {
								message += `- ${value}\n`
							}
							setFeedbackHandler({
								message: message,
								type: "error",
							})
							if (!dontReopenCondition) {
								handleScannerReOpen()
							}
						} else if (discardStatuses.includes(error.response?.status)) {
							// these statuses are presumed to be failures we don't want to save
							setComponentSelection(commissioningComponentSelection, "failed")
							setFeedbackHandler({
								message: "Failed to cache request while offline.",
								type: "info",
							})
							if (error.response?.statusText) {
								console.error(error.response?.statusText)
								const message = error.response?.statusText
								setFeedbackHandler({
									message: message,
									type: "error",
								})
							}
							if (!dontReopenCondition) {
								handleScannerReOpen()
							}
						} else {
							setComponentSelection(commissioningComponentSelection, "pending")
							const url = `/controllers/${controllerType}s/${controllerUuid}/components/${commissioningComponentSelection.urlName}/`
							const request = {
								url: url,
								method: HttpMethod.POST,
								payload: scannedData,
								queryArgs: {},
								headers: {},
								raiseException: true,
							}
							// format is (storeName, value, key)
							await (await offlineStorage).put("apiRequests", request, url)
							const message = `Successfully cached ${controllerType} ${commissioningComponentSelection.name} request to be sent later`
							setFeedbackHandler({
								message: message,
								type: "info",
							})
						}
					})
			}
			// reset scanned data to null after submitting
			setScannedData(null)
		}, [
			handleScannerReOpen,
			scannedData,
			commissioningComponentSelection,
			nodeIdReplacement,
			isMaster,
			masterUuid,
			nodeDetails,
			nodeLocId,
			setComponentSelection,
			nodeLocIdFromUrl,
			handleGraphQLSubmit,
			setFeedbackHandler,
			newDeviceId,
			warning,
		])

		return !scanning ? (
			<DialogContent className={`${deviceIdEntryMethod === "text" ? styles.content : styles.scannerContent}`}>
				{isMaster && (
					<>
						{!isDeviceIdAssignment && (
							<>
								<ScannerHeaderComponent
									scanning={scanning}
									handleScannerClose={handleScannerClose}
									isMaster={isMaster}
									masterUuid={masterUuid}
									masterDetails={masterDetails}
									nodeControllerUuidToDisplay={nodeControllerUuidToDisplay}
									userMessage={userMessage}
									clearFeedbackHandler={clearFeedbackHandler}
								/>
								<TextField
									id="deviceid-input"
									variant="outlined"
									className={styles.textInputBox}
									type="text"
									label={`${masterOrNode} ID`}
									onChange={handleIdChange}
									value={newDeviceId || ""}
									error={!!newDeviceId && inputFormattingError}
									helperText={inputValidationHelpText}
									autoFocus={deviceIdEntryMethod === "text" || !scannerSupported}
									InputProps={!isMaster ? { endAdornment } : {}}
								/>
								{!!warning && <p className={styles.warningText}>{warning}</p>}
								{newDeviceId !== "" && (
									<DialogActions className={styles.actions}>
										<Button onClick={handleClose} className={styles.actionButton}>
											Close
										</Button>
										<Button
											variant="contained"
											color="primary"
											type="submit"
											disabled={inputFormattingError || loading}
											className={styles.actionButton}
											onClick={handleGraphQLSubmit}
										>
											Accept
										</Button>
									</DialogActions>
								)}
							</>
						)}
						{isDeviceIdAssignment && (
							<>
								<ScannerHeaderComponent
									scanning={scanning}
									handleScannerClose={handleScannerClose}
									isMaster={isMaster}
									masterUuid={masterUuid}
									masterDetails={masterDetails}
									nodeControllerUuidToDisplay={nodeControllerUuidToDisplay}
									userMessage={userMessage}
									clearFeedbackHandler={clearFeedbackHandler}
								/>
								<SelectComponent
									isMaster={isMaster}
									masterComponentScanned={masterComponentScanned}
									nodeComponentScanned={nodeComponentScanned}
									nodeDetails={nodeDetails}
									masterUuid={masterUuid}
									nodeLocIdFromUrl={nodeLocIdFromUrl}
									handleNodeComponentSelection={handleComponentSelection}
									setDeviceIdEntryMethod={setDeviceIdEntryMethod}
									setNodeIdReplacement={setNodeIdReplacement}
									componentListSelectionHandler={componentListSelectionHandler}
								/>
								<RememberSelectionBtn
									handleRememberComponentSelection={handleRememberComponentSelection}
									rememberComponentSelection={rememberComponentSelection}
									handleComponentSelection={handleComponentSelection}
								/>
							</>
						)}
					</>
				)}
				{!isMaster && (
					<>
						<div className={styles.scannerModalContainer}>
							<ScannerHeaderComponent
								scanning={scanning}
								handleScannerClose={handleScannerClose}
								isMaster={isMaster}
								masterDetails={masterDetails}
								nodeControllerUuidToDisplay={nodeControllerUuidToDisplay}
								userMessage={userMessage}
								clearFeedbackHandler={clearFeedbackHandler}
							/>
							<TextField
								id="deviceid-input"
								variant="outlined"
								className={styles.textInputBox}
								type="text"
								label={`Enter new ${masterOrNode} ID`}
								onChange={handleIdChange}
								value={newDeviceId || ""}
								error={!!newDeviceId && inputFormattingError}
								helperText={inputValidationHelpText}
								autoFocus={deviceIdEntryMethod === "text" || !scannerSupported}
								InputProps={{ endAdornment }}
							/>
							{!!warning && (
								<div className={styles.warningTextWrapper}>
									<ErrorOutline fontSize="small" color="error" />
									<p className={styles.warningText}>{warning}</p>
								</div>
							)}
							{newDeviceId !== "" && (
								<DialogActions className={styles.actions}>
									<Button onClick={handleClose} className={styles.actionButton}>
										Close
									</Button>
									<Button
										variant="contained"
										color="primary"
										type="submit"
										disabled={inputFormattingError || loading}
										className={styles.actionButton}
										onClick={handleGraphQLSubmit}
									>
										Accept
									</Button>
								</DialogActions>
							)}
						</div>

						{isDeviceIdAssignment && (
							<>
								<div className={`${styles.scannerModalContainer}`}>
									<SelectComponent
										isMaster={isMaster}
										masterComponentScanned={masterComponentScanned}
										nodeComponentScanned={nodeComponentScanned}
										nodeDetails={nodeDetails}
										masterUuid={masterUuid}
										nodeLocIdFromUrl={nodeLocIdFromUrl}
										handleNodeComponentSelection={handleComponentSelection}
										setDeviceIdEntryMethod={setDeviceIdEntryMethod}
										setNodeIdReplacement={setNodeIdReplacement}
										componentListSelectionHandler={componentListSelectionHandler}
									/>
								</div>
								<div className={styles.nodeLocationNavContainer}>
									<NodeLocationNavigator
										handleScannerOpen={handleScannerOpen}
										commissioningComponentSelection={commissioningComponentSelection}
										positionAbsolute={false}
										displayOpenBtn={false}
									/>
								</div>
							</>
						)}
					</>
				)}
			</DialogContent>
		) : (
			<div className={styles.scannerParentContainer}>
				<ScannerHeaderComponent
					scanning={scanning}
					handleScannerClose={handleScannerClose}
					isMaster={isMaster}
					masterDetails={masterDetails}
					nodeControllerUuidToDisplay={nodeControllerUuidToDisplay}
					userMessage={userMessage}
					setUserMessage={clearFeedbackHandler}
				/>
				{deviceIdEntryMethod === "barCode" && (
					<ReactQuagga
						onDetected={handleCodeDetection}
						onClose={handleScannerClose}
						onError={(err) => handleScanError(err)}
					/>
				)}
				{deviceIdEntryMethod === "qrCode" && (
					<ReactQrReader
						onDetected={handleCodeDetection}
						onClose={handleScannerClose}
						onError={(err) => handleScanError(err)}
					/>
				)}
			</div>
		)
	},
)

DeviceIdInput.displayName = "DeviceIdInput"

DeviceIdInput.propTypes = {
	handleClose: PropTypes.func.isRequired,
	deviceIdEntryMethod: PropTypes.string,
	setDeviceIdEntryMethod: PropTypes.func,
	locIds: PropTypes.string.isRequired,
	handleAccept: PropTypes.func.isRequired,
	nodeDetails: PropTypes.object,
	masterDetails: PropTypes.object,
	nodeComponentScanned: PropTypes.object,
	nodeLocIdFromUrl: PropTypes.string,
}

const DialogDeviceIdInput = memo((props) => {
	const [deviceIdEntryMethod, setDeviceIdEntryMethod] = useState(null)
	const scannerSupported = useQuagga()
	const isOpen = props.locIds.length > 0
	const isDeviceIdAssignment = props.location.search.split("&").includes("overlay=deviceid-assignement")

	return (
		<Dialog
			open={isOpen}
			TransitionComponent={Transition}
			keepMounted={false}
			onClose={props.handleClose}
			aria-labelledby="device-id-input-title"
			aria-describedby="device-id-input-description"
			fullWidth={true}
			hideBackdrop={true}
		>
			{isOpen && (
				<DeviceIdInput
					nodeComponentScanned={props.nodeComponentScanned}
					masterComponentScanned={props.masterComponentScanned}
					isDeviceIdAssignment={isDeviceIdAssignment}
					scannerSupported={scannerSupported}
					deviceIdEntryMethod={deviceIdEntryMethod}
					setDeviceIdEntryMethod={setDeviceIdEntryMethod}
					{...props}
				/>
			)}
		</Dialog>
	)
})

DialogDeviceIdInput.displayName = "DialogDeviceIdInput"
DialogDeviceIdInput.propTypes = DeviceIdInput.propTypes

export default DialogDeviceIdInput
